import { formatEninKey, parseDbTimestamp } from "@repo/shared";
import type { EninLookupResult } from "@repo/shared/types";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { extractCompanyDetails, extractOwners, extractRoles } from "./enin-extract";
import { T } from "./enin-lookup-result.i18n";

type EninLookupResultPanelProps = {
  snapshot: EninLookupResult;
  onRerun: () => void;
  /** QP-184 — batch PEP/sanctions screening; absent = feature not wired (e.g. frozen). */
  onScreenPersons?: () => void;
  screeningPending?: boolean;
  screeningError?: string | null;
  isPending: boolean;
  disabled: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  lang: string;
};

export function EninLookupResultPanel({
  snapshot,
  onRerun,
  onScreenPersons,
  screeningPending = false,
  screeningError,
  isPending,
  disabled,
  style = "none",
  title,
  description,
  lang,
}: EninLookupResultPanelProps) {
  const t = useT(T);
  const getContainerClass = () => {
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  const { companyId, companyName, bundle, status, searchedAt } = snapshot;
  const details = extractCompanyDetails(bundle.company_details);
  const owners = extractOwners(bundle.share_ownership_ultimate);
  const roles = extractRoles(bundle.person_company_role);

  const activeNegativeCount = status.flags.filter((f) => f.isActive && f.isNegative).length;

  const headerFacts = [
    details.orgForm,
    details.employees !== null ? `${details.employees} ${t.employees.toLowerCase()}` : null,
    details.founded ? `${t.founded} ${details.founded}` : null,
  ].filter(Boolean);

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader title={title} description={description} />

      {/* Company header + risk badge */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{companyName || details.name || companyId}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
            {companyId}
          </span>
        </div>
        <span
          className={`px-2 py-1 rounded text-sm font-medium ${
            status.hasActiveNegativeFlags
              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
              : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
          }`}
        >
          {status.hasActiveNegativeFlags
            ? `${activeNegativeCount} ${
                activeNegativeCount === 1 ? t.activeNegativeFlag : t.activeNegativeFlags
              }`
            : t.noNegativeFlags}
        </span>
      </div>
      {headerFacts.length > 0 && (
        <p className="text-sm text-muted-foreground">{headerFacts.join(" · ")}</p>
      )}

      {/* Ownership */}
      <div className="text-sm border-t pt-3">
        <span className="font-medium">{t.ownership}</span>
        {owners.length > 0 ? (
          <div className="mt-2 space-y-1">
            {owners.map((owner) => (
              <div key={owner.name} className="flex items-center justify-between gap-2">
                <span>{owner.name}</span>
                <span className="text-muted-foreground tabular-nums">
                  {owner.stakePercent !== null ? `${owner.stakePercent.toFixed(1)} %` : "—"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-muted-foreground">{t.noData}</p>
        )}
      </div>

      {/* Roles */}
      <div className="text-sm border-t pt-3">
        <span className="font-medium">{t.roles}</span>
        {roles.length > 0 ? (
          <div className="mt-2 space-y-1">
            {roles.map((role) => (
              <div
                key={`${role.personName}-${role.role}`}
                className="flex items-center justify-between gap-2"
              >
                <span>{role.personName}</span>
                <span className="text-muted-foreground">
                  {role.role ? formatEninKey(role.role, lang) : "—"}
                  {!role.active && ` (${t.inactive})`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-muted-foreground">{t.noData}</p>
        )}
      </div>

      {/* QP-184 — AML screening of UBOs / senior roles / representative */}
      <div className="text-sm border-t pt-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium">{t.screeningTitle}</span>
          {onScreenPersons && !disabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={onScreenPersons}
              loading={screeningPending}
            >
              {snapshot.personScreening ? t.rescreen : t.screenAll}
            </Button>
          )}
        </div>
        {screeningError && <p className="text-destructive">{screeningError}</p>}
        {snapshot.personScreening ? (
          <div className="space-y-1">
            {snapshot.personScreening.persons.map((person) => {
              const kindLabel =
                person.kind === "ubo"
                  ? t.kindUbo
                  : person.kind === "role"
                    ? t.kindRole
                    : t.kindRepresentative;
              return (
                <div
                  key={`${person.kind}-${person.name}`}
                  className="flex flex-wrap items-center justify-between gap-2"
                >
                  <span>
                    {person.name}
                    <span className="text-muted-foreground">
                      {" "}
                      — {kindLabel}
                      {person.detail
                        ? ` (${person.kind === "role" ? formatEninKey(person.detail, lang) : person.detail})`
                        : ""}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    {person.isPEP && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                        {t.pepHit}
                      </span>
                    )}
                    {person.isSanctioned && (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200">
                        {t.sanctionHit}
                      </span>
                    )}
                    {!person.isPEP && !person.isSanctioned && (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                        {t.noHits}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground">
              <TimeAgoTooltip date={parseDbTimestamp(snapshot.personScreening.screenedAt)}>
                {getRelativeTime(snapshot.personScreening.screenedAt, lang)}
              </TimeAgoTooltip>
              {snapshot.personScreening.isTestMode && ` · ${t.screenedTestMode}`}
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground">{t.screeningHint}</p>
        )}
      </div>

      {/* Flags */}
      <div className="text-sm border-t pt-3">
        <span className="font-medium">{t.flags}</span>
        {status.flags.length > 0 ? (
          <div className="mt-2 space-y-1">
            {status.flags.map((flag) => (
              <div
                key={`${flag.code}-${flag.startDate}`}
                className={
                  flag.isActive && flag.isNegative
                    ? "text-red-700 dark:text-red-400"
                    : "text-muted-foreground"
                }
              >
                <span className="font-medium">{formatEninKey(flag.code, lang)}</span>
                {flag.description && ` — ${flag.description}`}
                {flag.startDate && ` (${flag.startDate})`}
                {!flag.isActive && ` · ${t.inactive}`}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-green-700 dark:text-green-400">{t.noFlags}</p>
        )}
      </div>

      {/* Lookup metadata */}
      <div className="text-xs text-muted-foreground border-t pt-3 flex items-center justify-between">
        <span>
          {t.lookup}: {companyId}
          {" • "}
          <TimeAgoTooltip date={parseDbTimestamp(searchedAt)}>
            {getRelativeTime(searchedAt, lang)}
          </TimeAgoTooltip>
        </span>
        {!disabled && (
          <Button variant="outline" size="sm" onClick={onRerun} disabled={isPending}>
            {t.rerunLookup}
          </Button>
        )}
      </div>
    </div>
  );
}
